import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { clientAPI } from "../api/client";
import { NumberOfPerPage } from "../utils/globalVariable";

const NUMBER_PER_PAGE = NumberOfPerPage.onLeave;
const queryKey = "CustomerCareList";

const initData = {
  data: [],
  totalPages: 0,
  entries: 0,
  startEntry: 0,
  endEntry: 0,
};

async function fetchCustomerCareList(
  currentPage,
  search,
  employeeId,
  rank,
  wishlistProjectId,
  transactedProjectId,
  sortBy,
  sortOrder,
) {
  try {
    const options = {};

    if (search !== undefined && search !== null && search !== "") {
      options.search = search;
    }
    if (employeeId) {
      options.employeeId = employeeId;
    }
    if (rank) {
      options.rank = rank;
    }
    if (wishlistProjectId?.length > 0) {
      options.wishlistProjectId = wishlistProjectId;
    }
    if (transactedProjectId?.length > 0) {
      options.transactedProjectId = transactedProjectId;
    }
    if (sortBy) {
      options.sortBy = sortBy;
      options.sortOrder = sortOrder || "desc";
    }

    const queryParams = new URLSearchParams({
      ...options,
      limit: NUMBER_PER_PAGE,
      page: currentPage,
    }).toString();

    const data = await clientAPI("get", `/customer/customer-care?${queryParams}`);

    const total = data?.pagination?.total || 0;
    const page = data?.pagination?.page || 1;
    const limit = data?.pagination?.limit || NUMBER_PER_PAGE;

    const totalPages = Math.ceil(total / NUMBER_PER_PAGE);
    const entries = total;
    const startEntry = total > 0 ? (page - 1) * limit + 1 : 0;
    const endEntry = Math.min(page * limit, entries);

    const newData = (data?.items || []).map((item) => ({
      _id: item?._id,
      code: item?.code || "-",
      name: item?.name || "-",
      phone: item?.phone || "-",
      email: item?.email || "-",
      isBusiness: !!item?.isBusiness,
      customerType: item?.customerType?.name || "-",
      employee: item?.employee?.fullName || "-",
      transactionCount: item?.transactionCount || 0,
      wishlistCount: item?.wishlistCount || 0,
      transactedProjectCount: item?.transactedProjectCount || 0,
      rankScore: item?.rankScore || 0,
      rank: item?.rank || "Thành viên",
    }));

    return {
      data: newData,
      totalPages,
      entries,
      startEntry,
      endEntry,
    };
  } catch (error) {
    console.error("Error fetching customer care data:", error);
    return initData;
  }
}

export function useCustomerCareList(
  search,
  employeeId,
  rank,
  wishlistProjectId,
  transactedProjectId,
  sortBy,
  sortOrder,
) {
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    employeeId,
    rank,
    wishlistProjectId,
    transactedProjectId,
    sortBy,
    sortOrder,
  ]);

  const { data, refetch, isLoading, isRefetching, isFetching } = useQuery({
    queryKey: [
      queryKey,
      currentPage,
      search,
      employeeId,
      rank,
      wishlistProjectId,
      transactedProjectId,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      fetchCustomerCareList(
        currentPage,
        search,
        employeeId,
        rank,
        wishlistProjectId,
        transactedProjectId,
        sortBy,
        sortOrder,
      ),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching customer care list data:", error);
    },
  });

  return {
    customerCareData: data?.data || initData.data,
    totalPages: data?.totalPages || initData.totalPages,
    refetch,
    isLoading,
    isRefetching: isRefetching || isFetching,
    setCurrentPage,
    currentPage,
    entries: data?.entries || initData.entries,
    startEntry: data?.startEntry || initData.startEntry,
    endEntry: data?.endEntry || initData.endEntry,
  };
}
